import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {
  defaultGetNextPageParam,
  sortObjectKeys,
} from '@vindr/utils/helpers/common.helper';
import { isIOS } from '@vindr/utils/mobile';
import {
  ParamsCreatePreStudy,
  createPreStudy,
  fetchStudyList,
  getDetailStudy,
} from '../apis/study.api';
import { queryKey } from '../constants/queryClientKey';
import { PUBLIC_VIEWER_URL, VIEWER_URL } from '../constants/study.constant';

let viewerWindow;

export const openViewer = (studyUID, modality = null) => {
  const isModalitySM =
    modality && (modality === 'SM' || modality.includes('SM'));
  const urlViewer = `${VIEWER_URL}/${
    isModalitySM ? 'microscopy' : 'viewer'
  }?StudyInstanceUIDs=${studyUID}`;

  if (isIOS()) {
    if (viewerWindow) viewerWindow.close();
    setTimeout(() => {
      viewerWindow = window.open(urlViewer, 'vindrViewerWindow');
    }, 0);
  } else {
    if (viewerWindow) viewerWindow.close();
    viewerWindow = window.open(urlViewer, 'vindrViewerWindow');
  }
};

export const openViewerWithHash = (hashInfo, modality) => {
  const isModalitySM =
    modality && (modality === 'SM' || modality.includes('SM'));
  const urlViewer = `${PUBLIC_VIEWER_URL}/${
    isModalitySM ? 'microscopy' : 'shared'
  }?${hashInfo}`;

  if (isIOS()) {
    if (viewerWindow) viewerWindow.close();
    setTimeout(() => {
      viewerWindow = window.open(urlViewer, 'vindrViewerWindow');
    }, 0);
  } else {
    if (viewerWindow) viewerWindow.close();
    viewerWindow = window.open(urlViewer, 'vindrViewerWindow');
  }
};

export const useGetStudiesInterval = (
  params: Parameters<typeof fetchStudyList>[0],
  options?: UseQueryOptions
) => {
  return useQuery(
    [queryKey.studies_interval, sortObjectKeys(params)],
    () => fetchStudyList(params),
    {
      refetchInterval: 10_000,
      keepPreviousData: true,
      ...options,
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof fetchStudyList>>>;
};

export const useGetStudiesInfinity = (
  params: Parameters<typeof fetchStudyList>[0],
  options?: UseInfiniteQueryOptions
) => {
  const queryInfo = useInfiniteQuery(
    [queryKey.studies_interval, sortObjectKeys(params)],
    (e) =>
      fetchStudyList({
        ...params,
        offset: (e.pageParam || 0) + (params?.offset || 0),
      }),
    {
      getNextPageParam: defaultGetNextPageParam,
      keepPreviousData: true,
      ...options,
    }
  ) as UseInfiniteQueryResult<Awaited<ReturnType<typeof fetchStudyList>>>;

  const data = queryInfo.data?.pages.map((page) => page.data || []).flat();

  return {
    ...queryInfo,
    data,
    count: queryInfo.data?.pages[0].count,
  };
};

export function useGetDetailStudy<T>(
  params: Parameters<typeof getDetailStudy>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.study_detail, sortObjectKeys(params)],
    () => getDetailStudy(params),
    {
      keepPreviousData: true,
      ...options,
      onError: (err) => {
        console.error('useGetDetailStudy', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getDetailStudy>>>;
}

export function useCreatePreStudy() {
  // const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreatePreStudy) => createPreStudy(params),
    {
      onError: (err) => {
        console.error('useSearchRole', err);
      },
    }
  );
}
